import invariant from "tiny-invariant";
import { t } from "@/i18n-js/instance";
import {
  ModalManager,
  useModal,
} from "@/react/components/Layout/ModalManagerProvider";
import type { MarketingEmailTemplateContentType } from "@/react/types/MarketingEmailTemplates";
import { ConfirmationModal } from "@circle-react-shared/ModalsV2/ConfirmationModal";
import { Typography } from "@circle-react-uikit/Typography";
import { useApplyTemplate } from "./hooks/useApplyTemplate";

const localeNamespace = "settings.emails.templates";

export const REPLACE_CONFIRMATION_MODAL_ID =
  "replace-confirmation-modal" as const;

export const ReplaceConfirmationModal = ModalManager.create(
  function ReplaceConfirmationModalComponent({
    templateId,
    templateContentType,
  }: {
    /** This field is required, but to allow for declarative usage of modal, it's placed optional.
     * The templateId will be passed in on the Modal.show() call.
     */
    templateId?: number;
    templateContentType?: MarketingEmailTemplateContentType;
  }) {
    const modal = useModal();
    const { onApplyTemplate, isApplyingTemplate } = useApplyTemplate();

    const handleConfirm = async () => {
      invariant(templateId, "templateId is required");
      await onApplyTemplate(templateId, templateContentType ?? "tip_tap_email");
      void modal.hide();
    };

    return (
      <ConfirmationModal
        isOpen={modal.visible}
        onClose={modal.hide}
        title={t([localeNamespace, "replace_existing_content"])}
        onConfirm={handleConfirm}
        confirmVariant="circle"
        confirmText={t("continue")}
        cancelText={t("cancel")}
        disabled={isApplyingTemplate}
      >
        <Typography.LabelMd>
          {t([localeNamespace, "replace_existing_content_description"])}
        </Typography.LabelMd>
      </ConfirmationModal>
    );
  },
);
